import styled from 'styled-components';
import * as animations from '../../styles/animations';

export const ComparisonSectionWrapper = styled.section<{ $isVisible?: boolean }>`
  padding: 120px 0;
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.02) 0%, rgba(255, 68, 68, 0.01) 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 10, 10, 0.2) 20%, rgba(10, 10, 10, 0.5) 40%, rgba(10, 10, 10, 0.8) 70%, rgba(10, 10, 10, 1) 100%);
    pointer-events: none;
    z-index: 1;
  }
`;

export const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 60px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const ComparisonCard = styled.div<{ $delay: number; $isVisible?: boolean }>`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 40px 32px;
  position: relative;
  overflow: hidden;

  opacity: ${props => props.$isVisible ? 1 : 0};
  filter: blur(${props => props.$isVisible ? '0px' : '12px'});
  transform: ${props => props.$isVisible ? 'translateY(0) scale(1) rotateX(0deg)' : 'translateY(80px) scale(0.85) rotateX(10deg)'};
  transition: opacity 1.8s cubic-bezier(0.16, 1, 0.3, 1),
              filter 1.8s cubic-bezier(0.16, 1, 0.3, 1),
              transform 1.8s cubic-bezier(0.16, 1, 0.3, 1),
              border-color 0.4s ease,
              box-shadow 0.4s ease;
  transition-delay: ${props => props.$delay * 0.25}s;
  transform-style: preserve-3d;
  perspective: 1000px;

  &:hover {
    border-color: rgba(255, 68, 68, 0.35);
    transform: translateY(-12px) scale(1.03);
    box-shadow: 0 24px 48px rgba(255, 68, 68, 0.15),
                0 0 0 1px rgba(255, 68, 68, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent 0%,
      #ff4444 25%,
      #ff6b6b 50%,
      #ff4444 75%,
      transparent 100%
    );
    background-size: 1000px 100%;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover::before {
    opacity: 1;
    animation: ${animations.shimmerAnimation} 2s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
      rgba(255, 68, 68, 0.08) 0%,
      transparent 50%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

export const ComparisonHeader = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;

  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: ${props => props.$isVisible ? 'scale(1)' : 'scale(0.9)'};
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.2s;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #ff4444, transparent);
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${ComparisonCard}:hover &::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const ComparisonLogo = styled.img.attrs({
  loading: 'lazy',
  decoding: 'async',
  width: '48',
  height: '48'
})`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 8px 24px rgba(255, 68, 68, 0.3);
  }
`;

export const ComparisonLogoWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    border-radius: 12px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    transform: scale(1.1) rotate(-5deg);

    svg {
      filter: drop-shadow(0 8px 24px rgba(255, 68, 68, 0.4));
    }
  }
`;

export const PlatformName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #a1a1aa;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.8;

  ${LogoContainer}:hover & {
    color: #ff4444;
    opacity: 1;
    transform: translateY(-2px);
    letter-spacing: 0.5px;
  }
`;

export const VsBadge = styled.div`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff4444;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  animation: ${animations.pulseBadge} 2s ease-in-out infinite;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 68, 68, 0.2);
    transform: scale(1.1);
    border-color: #ff4444;
  }
`;

export const ComparisonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ComparisonItem = styled.div<{ $type: 'loopra' | 'competitor'; $isVisible?: boolean; $index?: number }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: ${props => props.$type === 'loopra'
    ? 'rgba(0, 255, 136, 0.03)'
    : 'rgba(255, 165, 0, 0.03)'};
  border: 1px solid ${props => props.$type === 'loopra'
    ? 'rgba(0, 255, 136, 0.15)'
    : 'rgba(255, 165, 0, 0.15)'};

  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: ${props => props.$isVisible ? 'translateX(0)' : 'translateX(-30px)'};
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: ${props => props.$isVisible ? `${0.4 + (props.$index || 0) * 0.1}s` : '0s'};

  &:hover {
    background: ${props => props.$type === 'loopra'
    ? 'rgba(0, 255, 136, 0.08)'
    : 'rgba(255, 165, 0, 0.08)'};
    border-color: ${props => props.$type === 'loopra'
    ? 'rgba(0, 255, 136, 0.3)'
    : 'rgba(255, 165, 0, 0.3)'};
    transform: translateX(8px) scale(1.02);
    box-shadow: ${props => props.$type === 'loopra'
    ? '0 4px 16px rgba(0, 255, 136, 0.15)'
    : '0 4px 16px rgba(255, 165, 0, 0.15)'};
  }
`;

export const ComparisonIcon = styled.div<{ $type: 'loopra' | 'competitor' }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${props => props.$type === 'loopra' ? '#00ff88' : '#ffa500'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${ComparisonItem}:hover & {
    animation: ${animations.iconPop} 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 0 8px ${props => props.$type === 'loopra' ? '#00ff88' : '#ffa500'});
  }
`;

export const ComparisonText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #d4d4d8;
`;

export const ComparisonFooter = styled.div`
  margin-top: 60px;
  text-align: center;
  padding: 32px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  backdrop-filter: blur(12px);
  animation: ${animations.fadeInUp} 1s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  animation-delay: 0.8s;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 68, 68, 0.2);
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  }
`;

export const ComparisonFooterText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #a1a1aa;
  margin: 0;
  max-width: 900px;
  margin: 0 auto;

  strong {
    color: #ff4444;
    font-weight: 600;
  }
`;
